import { Component, EventEmitter, HostBinding, Injector, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, NgControl, ValidationErrors, Validators } from '@angular/forms';
import { ComponentUtility } from '@app/class/ComponentUtility';
import { IRadioButtonCard } from '../radio-button-cards/radio-button-cards.component';
import { IRadioButtonSmallCard } from '../radio-button-small-cards/radio-button-small-cards.component';
import "./radio-button-base.component.scss";

export interface IRadioButton<T = any> {
    label: string;
    description?: string;
    value: T;
    disabled?: boolean;
    hidden?: boolean;
    selected?: boolean;

    highlightColor?: string;
}

type RadioButton = IRadioButton | IRadioButtonCard | IRadioButtonSmallCard;

@Component({
    selector: 'v-radio-button-base',
    template: require('./radio-button-base.component.html'),
    providers: [
        ComponentUtility.createValueAccessorProvider(RadioButtonBaseComponent),
        ComponentUtility.createValidatorProvider(RadioButtonBaseComponent)
    ]
})
export class RadioButtonBaseComponent implements OnChanges {

    @Input() buttons: RadioButton[];
    @Input() allowMultiple: boolean;
    @Input() allowNone: boolean = true;
    @Input() layout: "column" | "row" = "row";
    @Input() inlineText: boolean;

    @Input() selected: RadioButton | RadioButton[];
    @Output() selectedChange = new EventEmitter<RadioButton | RadioButton[]>();

    @HostBinding("class.layout-row") get row() { return this.layout === "row" };
    @HostBinding("class.layout-column") get column() { return this.layout === "column" };

    @HostBinding("class.required") @Input() required: boolean;
    @HostBinding("class.disabled") @Input() disabled: boolean;

    constructor(
        protected readonly injector: Injector
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["buttons"] && this.allowMultiple) {
            this.selected = this.buttons.filter((button: IRadioButton) => button.selected);
        }

        if (changes["selected"] && !!this.selected && !Array.isArray(this.selected) && !this.allowMultiple) {
            this.selectButtonByValue(this.selected.value);
        }
    }

    onClickRadioButton(button: RadioButton): void {

        if (button.disabled) {
            return;
        }

        if (this.allowMultiple) {
            this.handleMultipleClick(button);
            return;
        }

        this.handleStandardClick(button);
    }

    // --- Control Value Accessor

    onChange: (value: any) => void;
    onTouched: () => void;
    control: NgControl;

    registerOnChange(callback: (value: any) => void): void {
        this.onChange = callback;
    }

    registerOnTouched(callback: () => void): void {
        this.onTouched = callback;
    }

    writeValue(button: RadioButton): void {
        this.selectButtonByValue(button?.value);

        this.onChange?.(button?.value);
    }

    setDisabledState(state: boolean) {
        this.disabled = state;
    }

    markAsTouched(): void {
        if (!this.onTouched || !this.control || this.control.touched) {
            return;
        }

        this.onTouched();
    }

    // Validator

    validate(control: AbstractControl): ValidationErrors | null {
        this.control = this.injector.get(NgControl);

        this.required = control.hasValidator(Validators.required);

        return null;
    }

    // ---

    private handleMultipleClick(button: RadioButton): void {

        const clickedButton = (button as IRadioButton);

        if (!Array.isArray(this.selected) || !this.selected) {
            this.selected = [];
        }

        const indexOfButton = this.selected.indexOf(clickedButton);

        if (indexOfButton > -1 && !this.canRemoveLastButton()) {
            return;
        }

        if (indexOfButton > -1 && clickedButton.selected) {
            clickedButton.selected = false;
            this.selected.splice(indexOfButton, 1);
        } else {
            clickedButton.selected = true;
            this.selected.push(button);
        }

        this.selectedChange.emit(this.selected);
        this.onChange?.(this.selected.map(selected => selected.value));
    }

    private canRemoveLastButton(): boolean {
        if (!this.allowNone && Array.isArray(this.selected) && this.selected.length == 1) {
            return false;
        }

        return true;
    }

    private handleStandardClick(button: RadioButton): void {
        if (this.selected === button) {
            return;
        }

        this.clearSelected();

        (button as IRadioButton).selected = true;

        this.selected = button;
        this.selectedChange.emit(button);
        this.onChange?.(button.value);
    }

    private clearSelected(): void {
        this.selected = null;
        this.buttons?.find((button: IRadioButton) => button.selected = false);
    }

    private selectButtonByValue(value: any): void {

        if (typeof value === "undefined" || value === null) {
            this.clearSelected();

            return;
        }

        this.selected = this.buttons?.find(button => button.value === value);
        (this.selected as IRadioButton).selected = true;
    }
}
